import { motion, useReducedMotion } from 'framer-motion';
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

import { useInterestingFacts, type AuthFact } from '../hooks/use-interesting-facts';

/** Per-character typing/erasing speed (ms). */
const TYPE_MS = 22;
const ERASE_MS = 10;
/** How long the fully-typed fact stays on screen before erasing (ms). */
const HOLD_MS = 11_000;
/** Cross-fade cycle interval used in the reduced-motion fallback (ms). */
const REDUCED_INTERVAL = 12_000;

type Phase = 'typing' | 'holding' | 'erasing';

/** Blinking caret shown at the end of the text while typing/holding. */
function Cursor() {
  return (
    <motion.span
      aria-hidden
      // text-bottom keeps the caret on the text baseline; a hair of negative
      // margin nudges it tight to the last glyph instead of sitting low.
      className="-mb-px ms-0.5 inline-block h-[1.05em] w-[2px] bg-white/90 align-text-bottom"
      animate={{ opacity: [1, 1, 0, 0] }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear', times: [0, 0.5, 0.5, 1] }}
    />
  );
}

/**
 * Tag pill — Figma node 524:5475: a translucent white fill (rgba 255/0.16),
 * fully rounded, 10px/8px padding, 12px medium white text. The design shows a
 * faint highlight on the ROUNDED END CAPS only (no border along the straight
 * top/bottom edges) — reproduced here as a soft horizontal gradient ring that's
 * gently lit within ~one cap-radius of each end and transparent through the
 * middle (pinned in px so it tracks the caps at any width). Painted as a 1px
 * pseudo-border via `mask` xor-compositing.
 */
function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="relative inline-flex items-center justify-center rounded-[20px] bg-white/[0.16] px-2.5 py-2 text-xs font-medium leading-none text-white">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[20px]"
        style={{
          // Thin (1px) ring lit (≈0.78) within one cap-radius of each end, fading
          // to nothing before the straight edges; a 2px blur turns it into the
          // design's soft, diffuse glow rim. `mask` xor punches the fill.
          padding: '1px',
          filter: 'blur(2px)',
          background:
            'linear-gradient(90deg, rgba(255,255,255,0.78) 0px, rgba(255,255,255,0.5) 9px, rgba(255,255,255,0.07) 16px, rgba(255,255,255,0) 22px, rgba(255,255,255,0) calc(100% - 22px), rgba(255,255,255,0.07) calc(100% - 16px), rgba(255,255,255,0.5) calc(100% - 9px), rgba(255,255,255,0.78) 100%)',
          WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />
      {children}
    </span>
  );
}

/**
 * "Interesting Facts" widget for the login hero. Typewriter + blinking cursor;
 * `prefers-reduced-motion` falls back to a timed cross-fade. The text reserves
 * min-height so wrapping never shifts the pill.
 */
export function FactsWidget() {
  const prefersReducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);

  // Live facts only (listInterestingFacts); empty/loading/error → render nothing.
  const facts = useInterestingFacts() ?? [];

  // Reset the cycle when the pool size changes so a stale index can't overrun it.
  useEffect(() => {
    setIndex(0);
  }, [facts.length]);

  // Avoid cycling an empty pool (a `% 0` would make the index NaN).
  if (facts.length === 0) return null;

  if (prefersReducedMotion) {
    return <FactsCrossfade facts={facts} index={index} setIndex={setIndex} />;
  }
  return <FactsTypewriter facts={facts} index={index} setIndex={setIndex} />;
}

// ── Shared shell — fixed position + reserved text height (no layout shift) ──

function FactsShell({ tag, children }: { tag: string; children: ReactNode }) {
  return (
    <div className="absolute inset-x-14 bottom-[11%] flex flex-col gap-4">
      <div dir="auto">
        <Tag>{tag}</Tag>
      </div>
      {/*
        Fixed height for exactly 3 lines (3 × leading-snug 1.375 ≈ 4.125em) so
        the block's total height never changes. Because it's pinned to the
        bottom, a *min*-height would let a 3-line fact grow the box upward and
        shove the pill up — a fixed height keeps the pill and text baseline
        static as facts type in and wrap.
      */}
      <p dir="auto" className="h-[4.2em] max-w-[420px] text-[13px] leading-snug text-white/90">
        {children}
      </p>
    </div>
  );
}

// ── Typewriter variant ──────────────────────────────────────────────────────

function FactsTypewriter({
  facts,
  index,
  setIndex,
}: {
  facts: AuthFact[];
  index: number;
  setIndex: Dispatch<SetStateAction<number>>;
}) {
  const fact = facts[index];
  const full = fact?.text ?? '';

  const [count, setCount] = useState(0);
  const [phase, setPhase] = useState<Phase>('typing');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (phase === 'typing') {
      if (count < full.length) {
        timer.current = setTimeout(() => {
          setCount((c) => c + 1);
        }, TYPE_MS);
      } else {
        timer.current = setTimeout(() => {
          setPhase('holding');
        }, 0);
      }
    } else if (phase === 'holding') {
      timer.current = setTimeout(() => {
        setPhase('erasing');
      }, HOLD_MS);
    } else {
      if (count > 0) {
        timer.current = setTimeout(() => {
          setCount((c) => c - 1);
        }, ERASE_MS);
      } else {
        setIndex((i) => (i + 1) % facts.length);
        setPhase('typing');
      }
    }
    return () => {
      clearTimeout(timer.current);
    };
  }, [phase, count, full.length, facts.length, setIndex]);

  if (!fact) return null;

  return (
    <FactsShell tag={fact.tag}>
      {full.slice(0, count)}
      <Cursor />
    </FactsShell>
  );
}

// ── Reduced-motion variant (cross-fade, no typing) ──────────────────────────

function FactsCrossfade({
  facts,
  index,
  setIndex,
}: {
  facts: AuthFact[];
  index: number;
  setIndex: Dispatch<SetStateAction<number>>;
}) {
  useEffect(() => {
    if (facts.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % facts.length);
    }, REDUCED_INTERVAL);
    return () => {
      clearInterval(id);
    };
  }, [facts.length, setIndex]);

  const fact = facts[index];
  if (!fact) return null;

  return <FactsShell tag={fact.tag}>{fact.text}</FactsShell>;
}
